"use strict";

(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root && root.document) api.bootstrap(root, root.document);
})(typeof window !== "undefined" ? window : null, function () {
  const EVENTS = Object.freeze({
    PAGE_VIEWED: "triumph:page_viewed",
    PAGE_SUMMARY: "triumph:page_summary",
    GALLERY_MEDIA_VIEWED: "triumph:gallery_media_viewed",
    GALLERY_MEDIA_SELECTED: "triumph:gallery_media_selected",
    SECTION_VIEWED: "triumph:section_viewed",
    SECTION_SUMMARY: "triumph:section_summary",
    FAQ_OPENED: "triumph:faq_opened",
    DISCLOSURE_OPENED: "triumph:disclosure_opened",
    HOTSPOT_OPENED: "triumph:hotspot_opened",
    MEDIA_PLAYED: "triumph:media_played",
    MEDIA_PROGRESS_REACHED: "triumph:media_progress_reached",
    REVIEWS_OPENED: "triumph:reviews_opened",
    OFFER_SELECTED: "triumph:offer_selected",
    CTA_CLICKED: "triumph:cta_clicked",
    NAVIGATION_CLICKED: "triumph:navigation_clicked",
    SCROLL_DEPTH_REACHED: "triumph:scroll_depth_reached"
  });
  const EVENT_NAMES = new Set(Object.values(EVENTS));
  const STORAGE_KEY = "triumph.site-analytics.v2";
  const MAX_PAYLOAD_BYTES = 1536;
  const HARD_EVENT_LIMIT = 96;
  const VIEW_MS = 1000;
  const ACTIVE_EXPOSURE = 0.25;
  const VIEW_EXPOSURE = 0.6;
  const EVENT_LIMITS = Object.freeze({
    [EVENTS.SECTION_VIEWED]: 32, [EVENTS.SECTION_SUMMARY]: 32,
    [EVENTS.CTA_CLICKED]: 20, [EVENTS.NAVIGATION_CLICKED]: 20,
    [EVENTS.SCROLL_DEPTH_REACHED]: 5
  });
  const SAFE_VALUE = /^[a-zA-Z0-9][a-zA-Z0-9._:/-]{0,159}$/;
  const COMMON_KEYS = new Set([
    "store_key", "product_key", "producer_schema_version", "theme_version",
    "content_version", "page_type", "page_key", "session_id", "page_view_id",
    "device_category", "referrer_category", "utm_source", "utm_medium", "utm_campaign"
  ]);
  const EVENT_KEYS = new Set([
    "section_id", "section_position", "media_id", "product_id", "action_id", "action_kind",
    "disclosure_id", "disclosure_kind", "hotspot_id", "progress_percent", "depth_percent",
    "active_ms", "elapsed_ms", "max_visible_percent", "max_scroll_percent", "sections_seen",
    "selection_source", "faq_id", "reviews_id", "offer_id", "variant_id", "destination_page_key", "navigation_area"
  ]);

  const now = (environment) => environment.performance && environment.performance.now
    ? environment.performance.now()
    : Date.now();
  const uuid = (environment) => environment.crypto && environment.crypto.randomUUID
    ? environment.crypto.randomUUID()
    : "00000000-0000-4000-8000-" + Math.random().toString(16).slice(2, 14).padEnd(12, "0");
  const safe = (value) => value == null || typeof value === "number" || typeof value === "boolean"
    || (typeof value === "string" && SAFE_VALUE.test(value));
  const sectionId = (element, index) => {
    const explicit = element && element.dataset && element.dataset.triumphSectionId;
    const raw = explicit || String((element && element.id) || "section-" + (index + 1)).split("__").pop();
    return raw.replace(/_/g, "-").replace(/[^a-zA-Z0-9-]/g, "-").replace(/-+/g, "-").toLowerCase();
  };
  // Normalize against what can fit in the viewport. Keep the actual element
  // fraction separately for max_visible_percent and use IO's clipped area.
  const attainableRatio = (rect, viewport) => rect && viewport && rect.width > 0 && rect.height > 0
    && viewport.width > 0 && viewport.height > 0
    ? Math.min(rect.width, viewport.width) * Math.min(rect.height, viewport.height) / (rect.width * rect.height)
    : 1;
  const deviceCategory = (environment) => environment.innerWidth < 600 ? "mobile" : environment.innerWidth < 1000 ? "tablet" : "desktop";
  const safeId = (value) => typeof value === "string" && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(value);
  // Custom behavioral IDs are compact identifiers; commerce envelopes own GIDs.
  const productId = (value) => String(value || "").replace(/^gid:\/\/shopify\/Product\//, "");
  const pageKey = (environment, explicit) => {
    if (safeId(explicit)) return explicit;
    const parts = String(environment.location.pathname || "/").split("/").filter(Boolean);
    if (parts.length === 0) return "home";
    // Known storefront handles only; never collect arbitrary paths or query strings.
    const offset = /^[a-z]{2}(?:-[a-z]{2})?$/i.test(parts[0]) ? 1 : 0;
    if (parts.length === offset) return "home";
    const route = parts[offset];
    if (["cart", "search"].includes(route)) return route;
    const handle = parts[offset + 1];
    const kinds = { products: "product", pages: "page", collections: "collection", blogs: "blog" };
    if (kinds[route] && /^[a-z0-9][a-z0-9-]{0,100}$/.test(handle || "")) {
      if (route === "blogs" && /^[a-z0-9][a-z0-9-]{0,100}$/.test(parts[offset + 2] || "")) {
        const article = "article:" + handle + ":" + parts[offset + 2];
        return safeId(article) ? article : "other";
      }
      return kinds[route] + ":" + handle;
    }
    return "other";
  };
  const attribution = (environment) => {
    const result = {};
    try {
      const url = new URL(environment.location.href);
      ["utm_source", "utm_medium", "utm_campaign"].forEach((key) => {
        const value = (url.searchParams.get(key) || "").trim().toLowerCase();
        if (/^[a-z0-9][a-z0-9._:-]{0,127}$/.test(value)) result[key] = value;
      });
    } catch (_error) { /* No raw URL or unknown query values enter an event. */ }
    return result;
  };
  const referrerCategory = (environment, documentObject, tags) => {
    if (/^(cpc|ppc|paid|paid-social|paid_social|display)$/.test(tags.utm_medium || "")) return "ads";
    if (tags.utm_medium === "email") return "email";
    if (!documentObject.referrer) return "direct";
    try {
      const host = new URL(documentObject.referrer).hostname.toLowerCase();
      if (host === environment.location.hostname) return "internal";
      if (/(^|\.)(google\.[a-z.]+|bing\.com|duckduckgo\.com|yahoo\.com)$/.test(host)) return "search";
      if (/(^|\.)(facebook\.com|instagram\.com|tiktok\.com|pinterest\.com|reddit\.com|twitter\.com|x\.com|t\.co)$/.test(host)) return "social";
      return "other";
    } catch (_error) {
      return "other";
    }
  };

  function buildPayload(environment, config, eventName, properties) {
    if (!EVENT_NAMES.has(eventName)) return null;
    const payload = { schema_version: 2, section_id: "page" };
    for (const [key, value] of Object.entries(config || {})) {
      if (COMMON_KEYS.has(key) && value !== "" && value != null) payload[key] = value;
    }
    for (const [key, value] of Object.entries(properties || {})) {
      if (!EVENT_KEYS.has(key) || value === "" || value == null || !safe(value)) return null;
      payload[key] = value;
    }
    if (!Object.values(payload).every(safe)) return null;
    const encoded = JSON.stringify(payload);
    const Encoder = environment.TextEncoder || (typeof TextEncoder !== "undefined" ? TextEncoder : null);
    if (!Encoder || new Encoder().encode(encoded).length > MAX_PAYLOAD_BYTES) return null;
    return payload;
  }

  class Producer {
    constructor(environment, documentObject, baseConfig) {
      this.environment = environment;
      this.document = documentObject;
      this.baseConfig = baseConfig;
      this.config = { ...baseConfig };
      this.active = false;
      this.eventCount = 0;
      this.eventCounts = new Map();
      this.startedAt = 0;
      this.pageActiveMs = 0;
      this.pageEnteredAt = null;
      this.pageSummary = null;
      this.suspended = false;
      this.maxScrollPercent = 0;
      this.scrollMilestones = new Set();
      this.sections = new Map();
      this.gallerySelections = new WeakMap();
      this.seen = new Set();
      this.scrollFrame = null;
      this.observer = null;
      this.resizeObserver = null;
      this.thresholdKey = null;
      this.viewTimer = null;
      this.boundConsent = () => this.syncConsent();
      this.boundVisibility = () => this.handleVisibility();
      this.boundPageHide = () => { this.suspended = true; this.pause(now(this.environment)); };
      this.boundPageShow = () => { this.suspended = false; this.resume(now(this.environment)); };
      this.boundResize = () => this.configureObserver();
      this.boundScroll = () => this.handleScroll();
      this.boundClick = (event) => this.handleClick(event, now(this.environment));
      this.boundGalleryChange = (event) => this.handleGalleryChange(event);
      this.boundChange = (event) => this.handleOfferChange(event);
      this.boundPlay = (event) => this.handleMediaPlay(event, now(this.environment));
      this.boundProgress = (event) => this.handleMediaProgress(event, now(this.environment));
      this.document.addEventListener("visitorConsentCollected", this.boundConsent);
    }

    privacyAllowed() {
      try {
        return this.environment.Shopify.customerPrivacy.analyticsProcessingAllowed() === true;
      } catch (_error) {
        return false;
      }
    }

    initializePrivacy() {
      const shopify = this.environment.Shopify || {};
      if (typeof shopify.loadFeatures === "function") {
        shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], () => this.syncConsent());
      } else {
        this.syncConsent();
      }
    }

    syncConsent() {
      if (this.privacyAllowed()) this.start();
      else if (this.active) this.stop();
    }

    start() {
      if (this.active) return;
      let stored = null;
      try { stored = this.environment.sessionStorage.getItem(STORAGE_KEY); } catch (_error) { /* In-memory session remains usable. */ }
      let sessionId = null;
      try { sessionId = stored ? JSON.parse(stored).session_id : null; } catch (_error) { sessionId = null; }
      sessionId = safeId(sessionId) ? sessionId : uuid(this.environment);
      const tags = attribution(this.environment);
      this.config = {
        ...this.baseConfig,
        product_id: productId(this.baseConfig.product_id),
        page_type: /^(home|product|page|collection|blog|article|search|cart|other)$/.test(this.baseConfig.page_type) ? this.baseConfig.page_type : "other",
        page_key: pageKey(this.environment, this.baseConfig.page_key),
        session_id: sessionId,
        page_view_id: uuid(this.environment),
        device_category: deviceCategory(this.environment),
        referrer_category: referrerCategory(this.environment, this.document, tags),
        ...tags
      };
      try { this.environment.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ session_id: sessionId })); } catch (_error) { /* Storage failure must not stop collection. */ }
      this.active = true;
      this.startedAt = now(this.environment);
      this.eventCount = 0;
      this.eventCounts.clear();
      this.maxScrollPercent = 0;
      this.scrollMilestones.clear();
      this.pageActiveMs = 0;
      this.pageSummary = null;
      this.suspended = false;
      this.pageEnteredAt = this.document.visibilityState === "hidden" ? null : this.startedAt;
      this.document.addEventListener("visibilitychange", this.boundVisibility);
      // Theme custom accordions toggle during their target listener. Observe
      // the pre-click state before those handlers run, as for native details.
      this.document.addEventListener("click", this.boundClick, { passive: true, capture: true });
      this.document.addEventListener("carousel:change", this.boundGalleryChange);
      this.document.addEventListener("change", this.boundChange, { passive: true, capture: true });
      this.document.addEventListener("play", this.boundPlay, true);
      this.document.addEventListener("timeupdate", this.boundProgress, true);
      this.environment.addEventListener("scroll", this.boundScroll, { passive: true });
      this.environment.addEventListener("resize", this.boundResize, { passive: true });
      this.environment.addEventListener("pagehide", this.boundPageHide);
      this.environment.addEventListener("pageshow", this.boundPageShow);
      this.observe();
      this.publish(EVENTS.PAGE_VIEWED, {});
    }

    stop() {
      if (!this.active) return;
      this.active = false;
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.observer = null;
      this.resizeObserver = null;
      this.thresholdKey = null;
      if (this.viewTimer != null) this.environment.clearTimeout(this.viewTimer);
      this.viewTimer = null;
      this.document.removeEventListener("visibilitychange", this.boundVisibility);
      this.document.removeEventListener("click", this.boundClick, true);
      this.document.removeEventListener("carousel:change", this.boundGalleryChange);
      this.document.removeEventListener("change", this.boundChange, true);
      this.document.removeEventListener("play", this.boundPlay);
      this.document.removeEventListener("timeupdate", this.boundProgress);
      this.environment.removeEventListener("scroll", this.boundScroll);
      this.environment.removeEventListener("resize", this.boundResize);
      this.environment.removeEventListener("pagehide", this.boundPageHide);
      this.environment.removeEventListener("pageshow", this.boundPageShow);
      if (this.scrollFrame != null) this.environment.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.sections.clear();
      this.seen.clear();
      try { this.environment.sessionStorage.removeItem(STORAGE_KEY); } catch (_error) { /* Revocation still disconnects collection. */ }
    }

    observe() {
      const explicit = Array.from(this.document.querySelectorAll("[data-triumph-observe='section']"));
      // A partly instrumented theme must not lose every unmarked section.
      // Prefer an explicit descendant over its Shopify wrapper, exactly once.
      const sections = explicit.filter((node, index) => !explicit.some((other, otherIndex) =>
        otherIndex !== index && other.contains && other.contains(node)
        && sectionId(other, otherIndex) === sectionId(node, index)
      ));
      for (const wrapper of this.document.querySelectorAll("main .shopify-section")) {
        if (!sections.some((node) => node === wrapper || (wrapper.contains && wrapper.contains(node)))) sections.push(wrapper);
      }
      sections.sort((left, right) => {
        const position = left.compareDocumentPosition ? left.compareDocumentPosition(right) : 0;
        if (position & 1) return 0;
        return position & 4 ? -1 : position & 2 ? 1 : 0;
      });
      const media = Array.from(new Set([
        ...this.document.querySelectorAll("[data-triumph-observe='media']"),
        ...this.document.querySelectorAll("product-gallery .product-gallery__media[data-triumph-media-id]")
      ]));
      // Approved/custom galleries may replace the native Liquid snippet. Their
      // explicit stable slide IDs remain valid even without the observe marker.
      for (const carousel of this.document.querySelectorAll("product-gallery media-carousel")) {
        const initial = carousel.querySelector(".product-gallery__media.is-selected[data-triumph-media-id]");
        if (initial) this.gallerySelections.set(carousel, initial.dataset.triumphMediaId);
      }
      sections.forEach((element, index) => this.sections.set(element, {
        id: sectionId(element, index), position: index + 1, ratio: 0, maxRatio: 0,
        enteredAt: null, qualifiedAt: null, activeMs: 0, viewed: false, summary: null, media: false
      }));
      media.forEach((element, index) => this.sections.set(element, {
        id: element.dataset?.triumphSectionId || element.closest?.("[data-triumph-observe=section]")?.dataset?.triumphSectionId || "main", position: sections.length + index + 1,
        ratio: 0, maxRatio: 0, enteredAt: null, qualifiedAt: null, activeMs: 0, viewed: false, summary: null,
        media: true, mediaId: element.dataset && element.dataset.triumphMediaId,
        productId: element.dataset && element.dataset.triumphProductId
      }));
      this.configureObserver();
      if (this.environment.ResizeObserver && this.observer) {
        this.resizeObserver = new this.environment.ResizeObserver(this.boundResize);
        this.sections.forEach((_state, element) => this.resizeObserver.observe(element));
      }
    }

    configureObserver(force = false) {
      if (!this.active || !this.environment.IntersectionObserver) return;
      const viewport = {
        width: this.document.documentElement.clientWidth || this.environment.innerWidth,
        height: this.document.documentElement.clientHeight || this.environment.innerHeight
      };
      const thresholds = new Set([0, ACTIVE_EXPOSURE, VIEW_EXPOSURE, 1]);
      this.sections.forEach((_state, element) => {
        const ratio = attainableRatio(element.getBoundingClientRect && element.getBoundingClientRect(), viewport);
        thresholds.add(ACTIVE_EXPOSURE * ratio);
        thresholds.add(VIEW_EXPOSURE * ratio);
      });
      const values = Array.from(thresholds).sort((left, right) => left - right);
      const key = JSON.stringify(values);
      if (!force && this.observer && key === this.thresholdKey) return;
      const timestamp = now(this.environment);
      // Discard stale exposure while new geometry is being observed. Resizing
      // must never let an old visibility sample complete a dwell timer.
      this.sections.forEach((state) => this.updateExposure(state, 0, 0, timestamp));
      if (this.observer) this.observer.disconnect();
      this.thresholdKey = key;
      const observer = new this.environment.IntersectionObserver((entries) => {
        if (!this.active || this.observer !== observer) return;
        this.consumeEntries(entries);
      }, { threshold: values });
      this.observer = observer;
      this.sections.forEach((_state, element) => observer.observe(element));
      this.scheduleView();
    }

    consumeEntries(entries) {
      const timestamp = now(this.environment);
      entries.slice().sort((a, b) => (a.time || 0) - (b.time || 0)).forEach((entry) => {
        const state = this.sections.get(entry.target);
        if (!state) return;
        const ratio = entry.isIntersecting ? Math.min(1, Math.max(0, Number(entry.intersectionRatio) || 0)) : 0;
        const exposure = Math.min(1, ratio / attainableRatio(entry.boundingClientRect, entry.rootBounds));
        // A delayed batch still preserves exits between short exposures.
        const at = Number.isFinite(entry.time) ? Math.min(timestamp, Math.max(this.startedAt, entry.time)) : timestamp;
        this.evaluate(at);
        this.updateExposure(state, ratio, exposure, at);
      });
      this.evaluate(timestamp);
      this.scheduleView();
    }

    updateExposure(state, ratio, exposure, timestamp) {
      state.ratio = ratio;
      const foreground = this.pageEnteredAt != null;
      if (foreground) state.maxRatio = Math.max(state.maxRatio, ratio);
      if (foreground && exposure >= ACTIVE_EXPOSURE) {
        if (state.enteredAt == null) state.enteredAt = Math.max(timestamp, this.pageEnteredAt);
      } else if (state.enteredAt != null) {
        state.activeMs += Math.max(0, timestamp - state.enteredAt);
        state.enteredAt = null;
      }
      if (foreground && exposure >= VIEW_EXPOSURE) {
        if (state.qualifiedAt == null) state.qualifiedAt = Math.max(timestamp, this.pageEnteredAt);
      } else state.qualifiedAt = null;
    }

    scheduleView() {
      if (this.viewTimer != null) this.environment.clearTimeout(this.viewTimer);
      this.viewTimer = null;
      if (!this.active || this.pageEnteredAt == null) return;
      let deadline = Infinity;
      this.sections.forEach((state) => {
        if (!state.viewed && state.qualifiedAt != null) deadline = Math.min(deadline, state.qualifiedAt + VIEW_MS);
      });
      if (!Number.isFinite(deadline)) return;
      this.viewTimer = this.environment.setTimeout(() => {
        this.viewTimer = null;
        // Drain pending threshold crossings before evaluating continuous dwell.
        if (this.observer && this.observer.takeRecords) this.consumeEntries(this.observer.takeRecords());
        this.evaluate(now(this.environment));
        this.scheduleView();
      }, Math.max(1, deadline - now(this.environment)));
    }

    evaluate(timestamp) {
      if (!this.active) return;
      this.sections.forEach((state) => {
        if (!state.viewed && state.qualifiedAt != null && timestamp - state.qualifiedAt >= VIEW_MS) {
          state.viewed = true;
          if (state.media) {
            this.publish(EVENTS.GALLERY_MEDIA_VIEWED, {
              section_id: state.id, media_id: state.mediaId || state.id,
              product_id: productId(state.productId || this.config.product_id)
            });
          } else {
            this.publish(EVENTS.SECTION_VIEWED, { section_id: state.id });
          }
        }
      });
    }

    publish(eventName, properties) {
      if (!this.active || this.eventCount >= HARD_EVENT_LIMIT) return false;
      const count = this.eventCounts.get(eventName) || 0;
      if (count >= (EVENT_LIMITS[eventName] || HARD_EVENT_LIMIT)) return false;
      const payload = buildPayload(this.environment, this.config, eventName, properties);
      if (!payload) return false;
      const transport = this.environment.Shopify && this.environment.Shopify.analytics;
      if (!transport || typeof transport.publish !== "function") return false;
      this.eventCount += 1;
      this.eventCounts.set(eventName, count + 1);
      try {
        const result = this.environment.Shopify.analytics.publish(eventName, payload);
        if (result && typeof result.catch === "function") result.catch(() => {});
      } catch (_error) { return false; }
      return true;
    }

    summarize(timestamp) {
      this.evaluate(timestamp);
      let sectionsSeen = 0;
      this.sections.forEach((state) => {
        if (state.media) return;
        const activeMs = state.activeMs + (state.enteredAt == null ? 0 : Math.max(0, timestamp - state.enteredAt));
        if (state.viewed) sectionsSeen += 1;
        if (state.maxRatio > 0) {
          const payload = {
            section_id: state.id, section_position: state.position,
            active_ms: Math.round(activeMs), max_visible_percent: Math.round(state.maxRatio * 100)
          };
          const signature = JSON.stringify(payload);
          if (state.summary !== signature && this.publish(EVENTS.SECTION_SUMMARY, payload)) state.summary = signature;
        }
      });
      const elapsed = Math.max(0, timestamp - this.startedAt);
      const pageActiveMs = this.pageActiveMs + (this.pageEnteredAt == null ? 0 : Math.max(0, timestamp - this.pageEnteredAt));
      const payload = {
        section_id: "page", active_ms: Math.round(pageActiveMs), elapsed_ms: Math.round(elapsed),
        max_scroll_percent: this.maxScrollPercent, sections_seen: sectionsSeen
      };
      // pagehide commonly follows visibilitychange; do not spend a second
      // event on a hidden-only elapsed-time change.
      const signature = JSON.stringify([payload.active_ms, payload.max_scroll_percent, sectionsSeen]);
      if (this.pageSummary !== signature && this.publish(EVENTS.PAGE_SUMMARY, payload)) this.pageSummary = signature;
    }

    handleVisibility() {
      if (this.document.visibilityState === "hidden") this.pause(now(this.environment));
      else if (!this.suspended) this.resume(now(this.environment));
    }

    pause(timestamp) {
      if (!this.active) return;
      if (this.observer && this.observer.takeRecords) this.consumeEntries(this.observer.takeRecords());
      this.evaluate(timestamp);
      this.sections.forEach((state) => this.updateExposure(state, 0, 0, timestamp));
      if (this.pageEnteredAt != null) this.pageActiveMs += Math.max(0, timestamp - this.pageEnteredAt);
      this.pageEnteredAt = null;
      this.scheduleView();
      this.summarize(timestamp);
    }

    resume(timestamp) {
      if (!this.active || this.document.visibilityState === "hidden" || this.pageEnteredAt != null) return;
      this.pageEnteredAt = timestamp;
      this.configureObserver(true);
    }

    handleScroll() {
      if (!this.active || this.scrollFrame != null) return;
      this.scrollFrame = this.environment.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const root = this.document.documentElement;
        const body = this.document.body;
        const documentHeight = Math.max(root.scrollHeight || 0, body.scrollHeight || 0);
        const viewportHeight = root.clientHeight || this.environment.innerHeight || 0;
        const percent = documentHeight > 0 ? Math.min(100, Math.round(((this.environment.scrollY || root.scrollTop || body.scrollTop || 0) + viewportHeight) * 100 / documentHeight)) : 100;
        this.maxScrollPercent = Math.max(this.maxScrollPercent, percent);
        [25, 50, 75, 100].forEach((milestone) => {
          if (percent >= milestone && !this.scrollMilestones.has(milestone)) {
            this.scrollMilestones.add(milestone);
            this.publish(EVENTS.SCROLL_DEPTH_REACHED, { depth_percent: milestone });
          }
        });
        this.evaluate(now(this.environment));
      });
    }

    once(key, eventName, properties) {
      if (this.seen.has(key)) return false;
      this.seen.add(key);
      return this.publish(eventName, properties);
    }

    handleClick(event, _timestamp) {
      if (!this.active || !event || !event.target) return;
      const target = event.target;
      const summary = typeof target.closest === "function" ? target.closest("[data-triumph-disclosure-id] > summary") : null;
      if (summary && summary.parentElement) {
        const disclosure = summary.parentElement;
        if (disclosure.open === true) return; // Click fires before the native toggle: this one closes it.
        const data = disclosure.dataset || {};
        this.once("disclosure:" + data.triumphDisclosureId, EVENTS.DISCLOSURE_OPENED, {
          section_id: data.triumphSectionId || "faq",
          disclosure_id: data.triumphDisclosureId,
          disclosure_kind: data.triumphDisclosureKind || "disclosure"
        });
        return;
      }
      const find = (selector) => typeof target.closest === "function" ? target.closest(selector) : null;
      const hotspot = find("[data-triumph-hotspot-id]");
      if (hotspot) {
        const data = hotspot.dataset || {};
        this.once("hotspot:" + data.triumphHotspotId, EVENTS.HOTSPOT_OPENED, {
          section_id: data.triumphSectionId || data.triumphParentSectionId || "hotspot",
          hotspot_id: data.triumphHotspotId
        });
        return;
      }
      const gallery = find("[data-triumph-media-select]");
      if (gallery) {
        const data = gallery.dataset || {};
        // Native carousel changes cover thumbnail, arrows, keyboard and swipes.
        // Do not double-count the click followed by the same carousel change.
        if (gallery.closest?.("product-gallery")?.querySelector("media-carousel")) return;
        this.publish(EVENTS.GALLERY_MEDIA_SELECTED, {
          section_id: data.triumphSectionId || "main", media_id: data.triumphMediaId || "gallery",
          product_id: productId(data.triumphProductId || this.config.product_id), selection_source: data.triumphSelectionSource || "control"
        });
        return;
      }
      const reviews = find("[data-triumph-reviews-control]");
      if (reviews) {
        this.publish(EVENTS.REVIEWS_OPENED, { section_id: (reviews.dataset || {}).triumphSectionId || "main", reviews_id: (reviews.dataset || {}).triumphReviewsId || "reviews" });
        return;
      }
      const offer = find("[data-triumph-offer-control]");
      if (offer) {
        this.publish(EVENTS.OFFER_SELECTED, { section_id: (offer.dataset || {}).triumphSectionId || "main", product_id: productId((offer.dataset || {}).triumphProductId || this.config.product_id), offer_id: (offer.dataset || {}).triumphOfferId || "offer" });
        return;
      }
      const cta = find("[data-triumph-cta-id]");
      if (cta) {
        if (cta.disabled || cta.getAttribute?.("aria-disabled") === "true") return;
        this.publish(EVENTS.CTA_CLICKED, { section_id: (cta.dataset || {}).triumphSectionId || "page", action_id: cta.dataset.triumphCtaId, action_kind: cta.dataset.triumphCtaKind || "primary_cta" });
        return;
      }
      const explicitNavigation = find("[data-triumph-navigation-id]");
      const navigation = explicitNavigation || find("a[href]");
      if (navigation) {
        let destination = "other";
        let area = navigation.dataset?.triumphNavigationArea || "navigation";
        let action = navigation.dataset?.triumphNavigationId;
        try {
          const url = new URL(navigation.href, this.environment.location.href);
          const current = new URL(this.environment.location.href);
          if (url.origin === current.origin) destination = pageKey({ location: url }, "");
          if (!explicitNavigation) {
            // Native internal links are dynamic navigation, not inferred copy
            // slots. Keep only known page keys; never store raw href/query/text.
            if (url.origin !== current.origin || destination === "other" ||
                url.pathname === current.pathname ||
                navigation.hasAttribute?.("download")) return;
            area = navigation.closest?.("header") ? "header" : navigation.closest?.("footer") ? "footer"
              : navigation.closest?.("main") ? "content" : navigation.closest?.("nav,[role=navigation]") ? "navigation" : null;
            if (!area) return;
            action = `navigation:${area}:${destination}`;
            if (!safeId(action)) return;
          }
        } catch (_error) { /* Do not send unknown destination URLs. */ }
        if (!action) return;
        this.publish(EVENTS.NAVIGATION_CLICKED, { section_id: "navigation", action_id: action,
          destination_page_key: destination, navigation_area: area });
        // Capture the last cumulative attention snapshot while the source
        // document is still alive. Do not prevent or delay native navigation.
        if ((!navigation.target || navigation.target === "_self") &&
            (event.button == null || event.button === 0) &&
            !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
          if (this.observer?.takeRecords) this.consumeEntries(this.observer.takeRecords());
          this.summarize(now(this.environment));
        }
      }
    }

    handleGalleryChange(event) {
      if (!this.active || this.document.visibilityState === "hidden") return;
      const carousel = event?.target;
      const slide = event?.detail?.slide;
      if (!carousel?.matches?.("product-gallery media-carousel") || !slide ||
          !carousel.contains(slide) || !slide.matches?.(".product-gallery__media[data-triumph-media-id]") ||
          slide.hidden) return;
      const mediaId = slide.dataset.triumphMediaId;
      if (!safeId(mediaId) || this.gallerySelections.get(carousel) === mediaId) return;
      this.gallerySelections.set(carousel, mediaId);
      const section = slide.closest?.("[data-triumph-observe=section]");
      this.publish(EVENTS.GALLERY_MEDIA_SELECTED, {
        section_id: slide.dataset.triumphSectionId || section?.dataset?.triumphSectionId || "main",
        media_id: mediaId, product_id: productId(slide.dataset.triumphProductId || this.config.product_id),
        // A transition can also follow a variant change. Never label all of
        // these as deliberate thumbnail clicks or unique people.
        selection_source: "carousel"
      });
    }

    handleOfferChange(event) {
      if (!this.active) return;
      const control = event && event.target;
      if (!control || control.disabled || control.type !== "radio" || control.checked !== true ||
          (control.dataset || {}).triumphOfferChange !== "true") return;
      const data = control.dataset;
      this.publish(EVENTS.OFFER_SELECTED, {section_id: data.triumphSectionId || "main",
        product_id: productId(data.triumphProductId || this.config.product_id), offer_id: data.triumphOfferId});
    }

    handleMediaPlay(event, _timestamp) {
      if (!this.active || !event || !event.target) return;
      const media = event.target;
      const hook = typeof media.closest === "function" ? media.closest("[data-triumph-media-id]") : null;
      if (!hook) return;
      const data = hook.dataset || {};
      this.once("play:" + data.triumphMediaId, EVENTS.MEDIA_PLAYED, {
        section_id: data.triumphSectionId || "media", media_id: data.triumphMediaId
      });
    }

    handleMediaProgress(event, _timestamp) {
      if (!this.active || !event || !event.target) return;
      const media = event.target;
      if (!(media.duration > 0)) return;
      const hook = typeof media.closest === "function" ? media.closest("[data-triumph-media-id]") : null;
      if (!hook) return;
      const data = hook.dataset || {};
      const percent = media.currentTime * 100 / media.duration;
      [25, 50, 75].forEach((milestone) => {
        if (percent >= milestone) this.once("progress:" + data.triumphMediaId + ":" + milestone, EVENTS.MEDIA_PROGRESS_REACHED, {
          section_id: data.triumphSectionId || "media", media_id: data.triumphMediaId,
          progress_percent: milestone
        });
      });
    }
  }

  function readConfig(documentObject) {
    const script = documentObject.querySelector("script[data-triumph-analytics]");
    const data = script && script.dataset ? script.dataset : {};
    return {
      store_key: data.triumphStoreKey || "",
      product_key: data.triumphProductKey || "",
      product_id: data.triumphProductId || "",
      producer_schema_version: data.triumphProducerSchemaVersion || "2",
      theme_version: data.triumphThemeVersion || "",
      content_version: data.triumphContentVersion || "",
      page_type: data.triumphPageType || "",
      page_key: data.triumphPageKey || ""
    };
  }

  function bootstrap(environment, documentObject) {
    if (!environment || !documentObject || !environment.Shopify) return null;
    const producer = new Producer(environment, documentObject, readConfig(documentObject));
    producer.initializePrivacy();
    return producer;
  }

  return { EVENTS, MAX_PAYLOAD_BYTES, HARD_EVENT_LIMIT, Producer, buildPayload, bootstrap };
});
